import React from 'react';

export class ModalDismissedError extends Error {
  constructor() {
    super('Modal dismissed before it was answered');
    this.name = 'ModalDismissedError';
  }
}

export const checkIsModalDismissed = (error: unknown) =>
  error instanceof ModalDismissedError;

export function useAsyncModalControls<T>(initState = false) {
  const [isOpen, setIsOpen] = React.useState(initState);
  const resolveRef = React.useRef<(value: T) => void | null>();
  const rejectRef = React.useRef<(reason: Error) => void | null>();

  const clearRefs = React.useCallback(() => {
    resolveRef.current = undefined;
    rejectRef.current = undefined;
  }, []);

  const openModal = React.useCallback(async () => {
    setIsOpen(true);
    return new Promise<T>((resolve, reject) => {
      rejectRef.current?.(new ModalDismissedError());

      resolveRef.current = resolve;
      rejectRef.current = reject;
    });
  }, []);

  const closeModal = React.useCallback(
    (value: T) => {
      setIsOpen(false);
      resolveRef.current?.(value);

      clearRefs();
    },
    [clearRefs],
  );

  React.useEffect(() => {
    return () => {
      rejectRef.current?.(new ModalDismissedError());
      clearRefs();
    };
  }, [clearRefs]);

  return {
    isOpen,
    openModal,
    closeModal,
    setIsOpen,
  };
}
